<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <v-row>
                <v-col cols="12" sm="12" md="8">
                    <h1>Marketplace Order</h1>
                </v-col>
                <v-col cols="12" sm="12" md="4">
                    <v-text-field
                        v-model="data.quickSearchVal"
                        :label="CommonLabelTextHelper.getLabelText.filterCurrentPage"
                        @keydown="onKeydownQuickSearch"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-expansion-panels v-model="data.panel" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-title>
                        <h2>Advanced Search</h2>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <date-range-select
                            v-model="data.searchFilterDateRangeGrp.dateOrderOn"
                            :from-label="formLabelText.dateOrderOnFrom"
                            :to-label="formLabelText.dateOrderOnTo"
                            :enable-validation="true"
                            @validation-change="onDateRangeValidationChange"
                        />
                        <v-row>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="data.searchFilter.orderId"
                                    label="Order ID"
                                    clearable
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="data.searchFilter.orderNumber"
                                    :label="formLabelText.orderNumber"
                                    clearable
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-select
                                    v-model="data.searchFilter.marketplaceCode"
                                    :items="marketplaceOptions"
                                    item-title="code"
                                    item-value="code"
                                    :label="formLabelText.marketplaceCode"
                                    clearable
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6" md="4">
                                <v-select
                                    v-model="data.searchFilter.orderBalanceSign"
                                    :items="data.orderBalanceSignOptions"
                                    :label="'Order Balance Sign'"
                                    clearable
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-select
                                    v-model="data.searchFilter.orderStatus"
                                    :items="orderStatusOptions"
                                    item-title="value"
                                    item-value="value"
                                    :label="formLabelText.orderStatus"
                                    clearable
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-select
                                    v-model="data.searchFilter.refundStatus"
                                    :items="refundStatusOptions"
                                    item-title="value"
                                    item-value="value"
                                    :label="formLabelText.refundStatus"
                                    clearable
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6" md="4">
                                <v-select
                                    v-model="data.searchFilter.paymentStatus"
                                    :items="paymentStatusOptions"
                                    item-title="value"
                                    item-value="value"
                                    :label="formLabelText.paymentStatus"
                                    clearable
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="12" md="8">
                                <v-text-field
                                    v-model="data.searchFilter.remarks"
                                    :label="formLabelText.remarks"
                                    clearable
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row class="mt-2">
                            <v-btn class="mr-2" variant="text" @click="onResetClick">
                                <v-icon>mdi-cached</v-icon>
                                Clear
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn class="mr-2" variant="text" @click="onSearchClick">
                                <v-icon>mdi-magnify</v-icon>
                                Search
                            </v-btn>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="data.deleteOperation.showConfirmDialog" max-width="500">
                <v-card>
                    <v-card-title class="headline">
                        <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                        Confirm Delete
                    </v-card-title>
                    <v-card-text>
                        Are you sure you want to delete this marketplace order?
                        <br /><br />
                        <strong>Order ID:</strong> {{ data.deleteOperation.selectedMarketplaceOrder?.orderId || '' }}
                        <br />
                        <strong>Order Number:</strong>
                        {{ data.deleteOperation.selectedMarketplaceOrder?.orderNumber || '' }} <br /><br />
                        <v-alert type="warning" variant="tonal"> This action cannot be undone. </v-alert>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="onCancelDelete" :disabled="data.deleteOperation.isLoading"> Cancel </v-btn>
                        <v-btn
                            color="error"
                            @click="onConfirmDelete"
                            :loading="data.deleteOperation.isLoading"
                            :disabled="data.deleteOperation.isLoading"
                        >
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-card>
                <v-card-title>
                    <div class="d-flex flex-wrap align-center ga-2 w-100">
                        <v-btn color="success" @click="onImportClick"> Create by import </v-btn>
                        <v-btn variant="text" @click="onSearchClick">
                            <v-icon>mdi-cached</v-icon>
                            Refresh
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn color="warning" @click="onImportMarketplaceOrderItemClick">
                            Import Item
                        </v-btn>
                        <v-btn color="primary" @click="onImportShippingFeeClick">
                            Import Shipping
                        </v-btn>
                        <v-btn color="warning" @click="onImportProductCostClick">
                            Import Product Cost
                        </v-btn>
                        <export-to-csv
                            :data="currentPageData"
                            :csv-header="data.csvExportConfig.csvHeader"
                            :filename="`exportMarketplaceOrder_page${pagination.currentPage}.csv`"
                            :formatter="csvFormatter"
                        ></export-to-csv>
                    </div>
                </v-card-title>
                <v-card-text>
                    <!-- Generic Table Component -->
                    <GenericListTable
                        :pagination="pagination"
                        :current-page-data="currentPageData"
                        :headers="headers"
                        :primary-key="'orderId'"
                        :action-header-value="actionHeaderItem.value"
                        :quick-search-value="data.quickSearchVal"
                        :loading-message="'Loading more marketplace orders...'"
                        :is-loading="data.deleteOperation.isLoading"
                        @page-size-change="onPageSizeChange"
                        @page-select="onPageSelect"
                        @next-page="onNextPage"
                        @previous-page="onPreviousPage"
                    >
                        <template #table-actions="{ item, isLoading }">
                            <v-btn
                                :disabled="isLoading"
                                variant="text"
                                icon="mdi-pencil"
                                @click="onUpdateMarketplaceOrderClick(item)"
                            >
                            </v-btn>
                            <v-btn
                                :disabled="isLoading"
                                variant="text"
                                icon="mdi-package-variant"
                                @click="onGetOrderItemSkusClick(item)"
                            >
                            </v-btn>
                            <v-btn
                                :disabled="isLoading"
                                :loading="isLoading"
                                variant="text"
                                icon="mdi-delete"
                                color="error"
                                @click="onDeleteMarketplaceOrderClick(item)"
                            >
                            </v-btn>
                        </template>
                    </GenericListTable>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import * as _ from 'lodash';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { MarketplaceOrderLabelTextHelper } from '../../utils/labelTextHelper/MarketplaceOrderLabelTextHelper';
import { CommonLabelTextHelper } from '../../utils/labelTextHelper/CommonLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { PathDefinition } from '../../utils/PathDefinition';
import DateRangeSelect from '../../components/DateRangeSelect.vue';
import AlertMessage from '../../components/AlertMessage.vue';
import ExportToCsv from '../../components/ExportToCsv.vue';
import GenericListTable from '../../components/GenericListTable.vue';
import { MarketplaceOrderHeaderHelper } from '../../utils/headerHelper/MarketplaceOrderHeaderHelper';
import { CsvFormatHelper } from '../../utils/CsvFormatHelper';
import { usePagination } from '../../composables/usePagination';
import { useEnums } from '../../composables/useEnums';
import { useMasterData } from '../../composables/useMasterData';
import { useUrlFilters } from '../../composables/useUrlFilters';
import { MarketplaceOrderFilterDefinitions } from '../../utils/filterDefinitions/MarketplaceOrderFilterDefinitions';

export default defineComponent({
    name: 'ListMarketplaceOrder',

    components: {
        ExportToCsv,
        AlertMessage,
        GenericListTable,
        DateRangeSelect
    },

    setup() {
        const route = useRoute();

        // Load enum options for dropdowns
        const { orderStatusOptions, paymentStatusOptions, refundStatusOptions, loadAllEnums } = useEnums();

        // Load master data options for dropdowns
        const { marketplaceOptions, loadAllMasterData } = useMasterData();

        const data = reactive({
            panel: [0],
            quickSearchVal: '',
            errorAlertMessage: '',
            searchFilterDateRangeGrpDefault: {},
            searchFilterDateRangeGrp: {
                dateOrderOn: {
                    from: null,
                    to: null
                }
            },
            searchFilterDefault: {},
            searchFilter: {
                orderId: null,
                marketplaceCode: null,
                orderNumber: null,
                orderStatus: null,
                refundStatus: null,
                paymentStatus: null,
                orderBalanceSign: null,
                remarks: null
            },
            orderBalanceSignOptions: [
                { title: 'Positive', value: 'positive' },
                { title: 'Negative', value: 'negative' },
                { title: 'Zero', value: 'zero' },
                { title: 'Non-Zero', value: 'non-zero' }
            ],
            csvExportConfig: {
                filename: 'exportMarketplaceOrder.csv',
                csvHeader: [...MarketplaceOrderHeaderHelper.getCsvHeader()]
            },
            deleteOperation: {
                isLoading: false,
                showConfirmDialog: false,
                selectedMarketplaceOrder: null
            },
            validation: {
                hasErrors: false
            }
        });

        // Initialize URL filter sync
        const { syncFiltersToUrl, restoreFiltersFromUrl, watchRouteChanges } = useUrlFilters({
            filterFields: MarketplaceOrderFilterDefinitions,
            onFiltersRestored: (filters) => {
                // Apply restored filters to reactive state
                Object.keys(filters).forEach((key) => {
                    if (key.startsWith('date') && (key.endsWith('From') || key.endsWith('To'))) {
                        const dateField = key.replace(/From|To$/, '');
                        const direction = key.endsWith('From') ? 'from' : 'to';
                        data.searchFilterDateRangeGrp[dateField][direction] = filters[key];
                    } else {
                        data.searchFilter[key] = filters[key];
                    }
                });
            },
            validationContext: {
                marketplaceOptions,
                orderStatusOptions,
                paymentStatusOptions,
                refundStatusOptions
            }
        });

        // Initialize pagination
        const {
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            updateItemInAllPages,
            removeItemFromAllPages,
            initialize
        } = usePagination({
            apiEndpoint: '/api/staff/marketplaceOrder/list',
            buildApiParams: (page: number) => {
                const params: any = {};

                // Add date range filtering if specified
                if (data.searchFilterDateRangeGrp.dateOrderOn.from) {
                    params.dateOrderOnFrom = Formatter.convertDateFromInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateOrderOn.from
                    );
                }

                if (data.searchFilterDateRangeGrp.dateOrderOn.to) {
                    params.dateOrderOnTo = Formatter.convertDateToInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateOrderOn.to
                    );
                }

                // Add order ID filter
                if (data.searchFilter.orderId?.trim()) {
                    params.orderId = data.searchFilter.orderId.trim();
                }

                // Add text filters
                if (data.searchFilter.marketplaceCode) {
                    params.marketplaceCode = data.searchFilter.marketplaceCode;
                }

                if (data.searchFilter.orderNumber) {
                    params.orderNumber = data.searchFilter.orderNumber;
                }

                if (data.searchFilter.orderStatus) {
                    params.orderStatus = data.searchFilter.orderStatus;
                }

                if (data.searchFilter.refundStatus) {
                    params.refundStatus = data.searchFilter.refundStatus;
                }

                if (data.searchFilter.paymentStatus) {
                    params.paymentStatus = data.searchFilter.paymentStatus;
                }

                if (data.searchFilter.orderBalanceSign) {
                    params.orderBalanceSign = data.searchFilter.orderBalanceSign;
                }

                if (data.searchFilter.remarks) {
                    params.remarks = data.searchFilter.remarks;
                }

                return params;
            }
        });

        onMounted(async () => {
            // Load enum options and master data FIRST (for dropdown validation)
            await Promise.all([loadAllEnums(), loadAllMasterData()]);

            // Clone defaults BEFORE restoring from URL
            data.searchFilterDateRangeGrpDefault = _.cloneDeep(data.searchFilterDateRangeGrp);
            data.searchFilterDefault = _.cloneDeep(data.searchFilter);

            // Restore filters from URL (if any)
            const hasUrlFilters = Object.keys(route.query).length > 0;
            if (hasUrlFilters) {
                restoreFiltersFromUrl();
            }

            // Initialize pagination (single API call with restored filters)
            await initialize();

            // Watch for future route changes (back/forward buttons)
            watchRouteChanges();
        });

        return {
            datepickerPreviewFormat: Formatter.defaultDatepickerPreviewFormat,
            formLabelText: MarketplaceOrderLabelTextHelper.getLabelText,
            CommonLabelTextHelper,
            actionHeaderItem: MarketplaceOrderHeaderHelper.getActionHeaderItem(),
            headers: [...MarketplaceOrderHeaderHelper.getHeaders()],
            courierCodeList: [],
            data,
            // Enum options for dropdowns
            orderStatusOptions,
            paymentStatusOptions,
            refundStatusOptions,
            // Master data options for dropdowns
            marketplaceOptions,
            // Pagination
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            updateItemInAllPages,
            removeItemFromAllPages,
            // URL Filters
            syncFiltersToUrl
        };
    },
    computed: {},
    methods: {
        onKeydownQuickSearch(evt: any) {
            if (evt.key === 'Enter') {
                // Quick search is now handled by GenericListTable component
            }
        },
        async onResetClick() {
            this.data.searchFilterDateRangeGrp = _.cloneDeep(this.data.searchFilterDateRangeGrpDefault);
            this.data.searchFilter = _.cloneDeep(this.data.searchFilterDefault);

            // Clear URL query params
            await this.syncFiltersToUrl({});

            // Reload with defaults
            this.resetPagination();
            await this.loadPage(1, true);
        },
        async onSearchClick() {
            if (this.data.validation.hasErrors) {
                this.data.errorAlertMessage = 'Please fix the date range errors before searching';
                this.errorAlert?.showAlert();
                return;
            }

            // Flatten filters for URL (handle nested date ranges)
            const currentFilters = {
                ...this.data.searchFilter,
                dateOrderOnFrom: this.data.searchFilterDateRangeGrp.dateOrderOn.from,
                dateOrderOnTo: this.data.searchFilterDateRangeGrp.dateOrderOn.to
            };

            // Sync to URL BEFORE API call
            await this.syncFiltersToUrl(currentFilters);

            // Reset pagination and search with new filters
            this.resetPagination();
            await this.loadPage(1, true);
        },
        onDateRangeValidationChange(validationStatus: { hasErrors: boolean }) {
            this.data.validation.hasErrors = validationStatus.hasErrors;
        },
        async onImportClick() {
            await this.$router.push({ path: PathDefinition.importMarketplaceOrder });
        },
        async onImportMarketplaceOrderItemClick() {
            await this.$router.push({ path: PathDefinition.importMarketplaceOrderItem });
        },
        async onImportShippingFeeClick() {
            await this.$router.push({ path: PathDefinition.importShippingFee });
        },
        async onImportProductCostClick() {
            await this.$router.push({ path: PathDefinition.importProductCost });
        },
        async onUpdateMarketplaceOrderClick(item: any) {
            await this.$router.push({ path: `/updateMarketplaceOrder/${item.orderId}` });
        },

        async onGetOrderItemSkusClick(item: any) {
            try {
                const orderId = item.orderId;
                const requestUrl = `/api/staff/marketplaceOrderItem/getByOrderId?orderId=${orderId}`;
                const response = await fetchWrapper.get(requestUrl, '');

                this.handleApiResponse(
                    response,
                    () => {
                        // Extract SKUs from order items and join them with line breaks
                        const orderItems = response.result || [];
                        const skus = orderItems.map((orderItem: any) => orderItem.sku).filter(Boolean);
                        const skuDisplay = skus.length > 0 ? skus.join('\n') : 'No item found';
                        this.updateItemInAllPages(item.orderId, 'orderId', 'orderItemSkuList', skuDisplay);
                    },
                    'Failed to fetch order item SKUs'
                );

                // If response fails, update with default value
                if (!response.success) {
                    this.updateItemInAllPages(item.orderId, 'orderId', 'orderItemSkuList', 'No item found');
                }
            } catch (error) {
                this.handleError(error, 'Error fetching order item SKUs', false);
                this.updateItemInAllPages(item.orderId, 'orderId', 'orderItemSkuList', 'No item found');
            }
        },

        // Utility method for handling API responses
        handleApiResponse(response: any, successCallback: () => void, errorMessage?: string) {
            if (response.success) {
                successCallback();
            } else {
                const message = errorMessage || response.errors || 'Operation failed';
                this.handleError(response.errors, message);
            }
        },
        csvFormatter: function (value: unknown, key: string, row: Record<string, unknown>): unknown {
            return CsvFormatHelper.formatDateTimeCsvValue(value as string, key);
        },
        onDeleteMarketplaceOrderClick(item: any) {
            this.data.deleteOperation.selectedMarketplaceOrder = item;
            this.data.deleteOperation.showConfirmDialog = true;
        },
        onCancelDelete() {
            this.data.deleteOperation.showConfirmDialog = false;
            this.data.deleteOperation.selectedMarketplaceOrder = null;
        },
        async onConfirmDelete() {
            if (!this.data.deleteOperation.selectedMarketplaceOrder) {
                return;
            }

            this.data.deleteOperation.isLoading = true;

            try {
                const orderId = this.data.deleteOperation.selectedMarketplaceOrder.orderId;
                const requestUrl = `/api/staff/marketplaceOrder/batchDelete`;
                const requestData = {
                    orderIdList: [orderId]
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                this.handleApiResponse(
                    response,
                    () => {
                        // Remove the item using the pagination composable
                        this.removeItemFromAllPages(orderId, 'orderId');

                        // Close dialog and reset state
                        this.data.deleteOperation.showConfirmDialog = false;
                        this.data.deleteOperation.selectedMarketplaceOrder = null;
                    },
                    'Failed to delete marketplace order'
                );
            } catch (error) {
                this.handleError(error, 'An unexpected error occurred while deleting the marketplace order');
            } finally {
                this.data.deleteOperation.isLoading = false;
            }
        },
        // Pagination event handlers
        async onNextPage() {
            await this.nextPage();
        },

        onPreviousPage() {
            this.previousPage();
        },

        onPageSelect(selectedPage: number) {
            this.selectPage(selectedPage);
        },

        async onPageSizeChange() {
            await this.changePageSize();
        },

        // Utility method for handling errors consistently
        handleError(error: any, message: string, showAlert: boolean = true) {
            console.error(message, error);
            if (showAlert) {
                this.data.errorAlertMessage = typeof error === 'string' ? error : message;
                this.$refs.errorAlert.showAlert();
            }
        }
    }
});
</script>

<style>
.validation-error {
    color: red;
}
</style>
